import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useAssignmentFindPlannedAssignmentsToSendToFieldTestersCounts,
  useAssignmentSendPlannedAssignmentsToFieldTesters,
} from 'api/assignment/assignment';
import Modal from 'components/Modal';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { useAssignmentsFilters } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import { FC, useEffect } from 'react';
import styled from 'styled-components';
import ToggleableBareAssignmentsList from './TogglableBareAssignmentsList';
import { formatDate } from 'utils/date-helpers';
import Roles, { getRolesForHandlingCaseType } from 'constants/Roles';
import { CaseTypeEnum } from 'api/model';
import ToggleableBareAssignmentRequestsList from './TogglableBareAssignmentRequestsList';

const Vertical = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const InfoSpan = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.foreground.tint};
  max-width: 700px;
`;

const Wrapper = styled.div`
  margin-bottom: 1rem;
  font-size: 1.2rem;
  p {
    margin-bottom: 0.5rem;
  }
`;

interface Props {
  onAssignmentsSent(): void;
  onCancel(): void;
  dateRange?: [Date | null, Date | null];
  caseType?: CaseTypeEnum;
  includeCaseTypes?: CaseTypeEnum[];
  excludeCaseTypes?: CaseTypeEnum[];
}

const SendCasesToFieldTestersModal: FC<Props> = ({
  onAssignmentsSent,
  onCancel,
  dateRange,
  caseType,
  includeCaseTypes,
  excludeCaseTypes,
}) => {
  const {
    state: { date },
  } = useAssignmentsFilters();

  // TODO: remove the date from asignmentsFilters
  const useDate = dateRange?.[1] ?? date;
  const {
    mutateAsync: sendPlannedAssignmentsToFieldTestersCall,
    status: sendPlannedAssignmentsToFieldTestersStatus,
  } = useAssignmentSendPlannedAssignmentsToFieldTesters();

  const {
    data,
    status: totalAssignmentsToSendStatus,
    mutateAsync: findPlannedAssignmentsCall,
  } = useAssignmentFindPlannedAssignmentsToSendToFieldTestersCounts();

  const makeFindPlannedAssignmentsCall = async () => {
    await findPlannedAssignmentsCall({
      data: {
        includeCaseTypes,
        excludeCaseTypes,
        date: useDate,
      },
    });
  };

  useEffect(() => {
    if (useDate) {
      makeFindPlannedAssignmentsCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, useDate]);

  const totalAssignmentsToSend = data?.validAssignmentsCount ?? 0;
  const totalAssignmentRequestsToSend = data?.assignmentRequestsCount ?? 0;
  const assignmentsInNeedOfApprovalCount = data?.invalidAssignmentsCount ?? 0;
  const placeholdersWithMissingExpensesCount =
    data?.placeholderAssignmentsWithMissingExpensesCount ?? 0;

  const missingFullDayPlanningCount = data?.missingFullDayPlanning ?? 0;

  const handleSendToFieldTesters = async () => {
    await sendPlannedAssignmentsToFieldTestersCall({
      data: {
        includeCaseTypes,
        excludeCaseTypes,
        date: useDate,
      },
    });
    onAssignmentsSent();
  };

  const getContent = () => {
    if (sendPlannedAssignmentsToFieldTestersStatus === 'loading') {
      return <LoadingSpinner>Skickar ut uppdrag...</LoadingSpinner>;
    }

    switch (totalAssignmentsToSendStatus) {
      case 'loading':
        return <LoadingSpinner />;

      case 'success':
        return (
          <Wrapper>
            <p>
              <b>{totalAssignmentsToSend}</b> planerade uppdrag kommer att
              skickas till fälttestarna.
              <br />
              <ToggleableBareAssignmentsList
                assignments={data?.validAssignments ?? []}
              />
            </p>
            <p>
              <b>{totalAssignmentRequestsToSend}</b> lagda annonsförfrågningar
              kommer att visas för fälttestarna.
              <br />
              <ToggleableBareAssignmentRequestsList
                requests={data?.assignmentRequests ?? []}
              />
            </p>

            {assignmentsInNeedOfApprovalCount > 0 && (
              <div>
                <b>{assignmentsInNeedOfApprovalCount}</b> uppdrag kommer att
                skickas till Mathias Frick för godkännande.
                <br />
                <ToggleableBareAssignmentsList
                  assignments={data?.invalidAssignments ?? []}
                />
              </div>
            )}

            {placeholdersWithMissingExpensesCount > 0 && (
              <Vertical>
                <span>
                  <b>{placeholdersWithMissingExpensesCount}</b>{' '}
                  utfyllnadsuppdrag saknar förväntade kostnader.
                  <br />
                  <ToggleableBareAssignmentsList
                    assignments={
                      data?.placeholderAssignmentsWithMissingExpenses ?? []
                    }
                  />
                </span>
                <InfoSpan>
                  Uppdrag som saknar förväntade kostnader kommer att skickas ut
                  ändå, men glöm inte att ladda upp kostnaderna innan uppdragen
                  slutförs.
                </InfoSpan>
              </Vertical>
            )}

            {missingFullDayPlanningCount > 0 && (
              <Vertical>
                <span>
                  <b>{missingFullDayPlanningCount}</b> uppdrag saknar
                  fullständig dagsplanering.
                  <br />
                  <ToggleableBareAssignmentsList
                    assignments={data?.missingFullDayPlanningAssignments ?? []}
                  />
                </span>
                <InfoSpan>
                  Uppdrag för FT som saknar fullständig dagsplanering kommer
                  endast att skickas ut om det gäller intradag eller om FT
                  tillåts bli planerad halvdagar, annars behöver du lösa
                  dagsplaneringen innan du kan skicka ut dem.
                </InfoSpan>
              </Vertical>
            )}

            <br />
            <p>Är du säker på att du vill fortsätta?</p>
          </Wrapper>
        );

      default:
        return null;
    }
  };

  const getButtons = () => {
    if (sendPlannedAssignmentsToFieldTestersStatus === 'loading') {
      return [];
    }

    return [
      {
        label: 'Avbryt',
        onClick: () => onCancel(),
        roles: caseType && getRolesForHandlingCaseType(caseType),
      },
      {
        icon: <FontAwesomeIcon icon={faPaperPlane} />,
        label: 'Skicka',
        onClick: handleSendToFieldTesters,
        roles:
          caseType === CaseTypeEnum.TestDrive
            ? [Roles.Admin, Roles.Riddermark]
            : [Roles.Admin],
      },
    ];
  };

  return (
    <Modal
      buttons={getButtons()}
      closeOnOutsideClick
      title={`Skicka ut till fälttestare för ${formatDate(useDate)}`}
    >
      {getContent()}
    </Modal>
  );
};

export default SendCasesToFieldTestersModal;
